import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UserTableComponent } from '../user-table/user-table.component';
import { LicenseOverviewCardComponent } from '../../../../payment/subscription/components/license-overview-card/license-overview-card.component';
import { UsersResponseDTO } from '../../models/UserDTO';
import { UserUIModel } from '../../models/UserUIModel';
import { Subject, takeUntil, map, catchError, EMPTY, merge } from 'rxjs';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { toUserUIModel } from '../../mappers/dto-mappers';
import { UserCreationNotificationService } from '../../services/user-creation-notification.service';
import { UserService } from '../../services/user.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { HrisIntegrationCardComponent } from '../../../organization/components/hris-integration-card/hris-integration-card.component';
import { HrisIntegrationDataService } from '../../../organization/services/hris-integration-data.service';
import { FeatureFlagsService } from '../../../../../core/services/feature-flag.service';

@Component({
  selector: 'app-user-management-overview',
  imports: [
    UserTableComponent,
    LicenseOverviewCardComponent,
    HrisIntegrationCardComponent,
    LoadingComponent,
  ],
  templateUrl: './user-management-overview.component.html',
})
export class UserManagementOverviewComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly userCreationNotificationService = inject(
    UserCreationNotificationService,
  );
  private readonly snackbarService = inject(SnackbarService);
  private readonly hrisIntegrationDataService = inject(
    HrisIntegrationDataService,
  );
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly router = inject(Router);

  users = signal<UserUIModel[]>([]);
  isLoading = signal(true);
  private readonly destroy$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.fetchUsers();
    merge(
      this.userCreationNotificationService.getUserChangedNotification(),
      this.hrisIntegrationDataService.syncCompletedObservable$,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldFetch) => {
        if (shouldFetch) {
          this.fetchUsers();
        }
      });
  }

  isHRISEnabled() {
    return this.featureFlagsService.isFeatureEnabled(
      this.featureFlagsService.hris,
    );
  }

  fetchUsers() {
    this.isLoading.set(true);
    this.userService
      .getUsers()
      .pipe(
        map((res: UsersResponseDTO) => toUserUIModel(res.users)),
        catchError((error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((users) => {
        this.users.set(users);
        this.isLoading.set(false);
      });
  }

  onAddLicensesButtonClick(): void {
    void this.router.navigate(['/portal/billing']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
